import { getNestedObjectProperty } from '@/core/utils/object';

export enum TempItemStatus {
  Added = 'Added',
  Updated = 'Updated',
  Removed = 'Removed',
}

export type TempItem<T> = (Partial<T> & { tempItemStatus: TempItemStatus | undefined }) | undefined;

export type WithReplica<P, K extends keyof P, T extends Record<string, unknown>> = Partial<Omit<P, K>> & {
  [Key in keyof P as Key extends K ? Key : never]: ReplicasList<T>;
};

export type ReplicasList<T extends Record<string, unknown>> = {
  data: T[] | undefined;
  tempItem: TempItem<T>;
};

export const emptyReplicasList = <T extends Record<string, unknown>>(): ReplicasList<T> => ({
  data: undefined,
  tempItem: undefined,
});

export const fillReplicasList = <T extends Record<string, unknown>>(
  dataList: T[],
  tempItem: TempItem<T>,
  idItemKey: string,
): ReplicasList<T> => {
  if (!tempItem) {
    return {
      data: dataList,
      tempItem: undefined,
    };
  }

  const existingItemIndex = dataList.findIndex(
    (dataItem) => getNestedObjectProperty(dataItem, idItemKey) === getNestedObjectProperty(tempItem, idItemKey),
  );

  const status = tempItem.tempItemStatus;

  if ((status === TempItemStatus.Added || status === TempItemStatus.Updated) && existingItemIndex === -1) {
    dataList.push(tempItem as Required<T>);
  } else if (status === TempItemStatus.Updated && existingItemIndex >= 0) {
    dataList.splice(existingItemIndex, 1, tempItem as Required<T>);
  } else if (status === TempItemStatus.Removed && existingItemIndex >= 0) {
    dataList.splice(existingItemIndex, 1);
  }

  return {
    data: dataList,
    tempItem: undefined,
  };
};
